import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LawyerSidebarNavigation from '../../components/ui/LawyerSidebarNavigation';
import ConsultationCard from './components/ConsultationCard';
import StatsWidget from './components/StatsWidget';
import ActivityFeed from './components/ActivityFeed';
import UpcomingAppointments from './components/UpcomingAppointments';
import QuickActions from './components/QuickActions';
import ConsultationFilters from './components/ConsultationFilters';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const LawyerDashboard = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [filteredConsultations, setFilteredConsultations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    urgency: 'all',
    category: 'all'
  });

  // Mock data for consultations
  const mockConsultations = [
    {
      id: 1,
      clientName: "علی احمدی",
      clientEmail: "ali.ahmadi@email.com",
      question: `سلام وکیل محترم، من یک قرارداد کاری دارم که در آن شرایط خاصی وجود دارد که نمی‌دانم آیا قانونی است یا خیر. کارفرما از من می‌خواهد که در صورت ترک کار، مبلغ ۵ میلیون تومان به عنوان جریمه پرداخت کنم. آیا این شرط قانونی است؟`,
      urgency: 'high',
      category: 'حقوق کار',
      submittedAt: '۲ ساعت پیش',
      messageCount: 3,
      deadline: new Date(Date.now() + 6 * 60 * 60 * 1000),
      status: 'pending'
    },
    {
      id: 2,
      clientName: "فاطمه کریمی",
      clientEmail: "fateme.karimi@email.com",
      question: `درود، من و همسرم تصمیم به طلاق توافقی گرفته‌ایم. فرزند ۸ ساله‌ای داریم و می‌خواهیم بدانیم چه مراحلی باید طی کنیم و حضانت فرزند چگونه تعیین می‌شود؟`,
      urgency: 'medium',
      category: 'حقوق خانواده',
      submittedAt: '۵ ساعت پیش',
      messageCount: 1,
      deadline: new Date(Date.now() + 24 * 60 * 60 * 1000),
      status: 'in_progress'
    },
    {
      id: 3,
      clientName: "محمد رضایی",
      clientEmail: "mohammad.rezaei@email.com",
      question: `سلام، من یک مغازه اجاره کرده‌ام و مالک بدون اطلاع قبلی اجاره را ۳۰ درصد افزایش داده است. آیا این کار قانونی است؟ چه حقوقی دارم؟`,
      urgency: 'low',
      category: 'حقوق مدنی',
      submittedAt: '۱ روز پیش',
      messageCount: 5,
      deadline: new Date(Date.now() + 48 * 60 * 60 * 1000),
      status: 'completed'
    },
    {
      id: 4,
      clientName: "زهرا محمدی",
      clientEmail: "zahra.mohammadi@email.com",
      question: `درود بر شما، شرکت ما با یک تامین‌کننده قرارداد داشته که متاسفانه نتوانسته تعهداتش را انجام دهد. می‌خواهیم قرارداد را فسخ کنیم. راهکار قانونی چیست؟`,
      urgency: 'high',
      category: 'حقوق تجاری',
      submittedAt: '۳ ساعت پیش',
      messageCount: 2,
      deadline: new Date(Date.now() + 12 * 60 * 60 * 1000),
      status: 'pending'
    }
  ];

  // Mock data for stats
  const mockStats = [
    {
      title: 'مشاوره‌های امروز',
      value: '12',
      change: 8,
      changeType: 'positive',
      icon: 'MessageSquare',
      color: 'primary'
    },
    {
      title: 'درآمد این ماه',
      value: '۲۴,۵۰۰,۰۰۰',
      change: 15,
      changeType: 'positive',
      icon: 'DollarSign',
      color: 'success'
    },
    {
      title: 'رضایت مشتریان',
      value: '۴.۸',
      change: 2,
      changeType: 'positive',
      icon: 'Star',
      color: 'warning'
    },
    {
      title: 'پروژه‌های فعال',
      value: '۸',
      change: -5,
      changeType: 'negative',
      icon: 'Briefcase',
      color: 'secondary'
    }
  ];

  // Mock data for activities
  const mockActivities = [
    {
      id: 1,
      type: 'consultation',
      description: 'مشاوره جدید از علی احمدی دریافت شد',
      timestamp: new Date(Date.now() - 30 * 60 * 1000),
      actionRequired: true
    },
    {
      id: 2,
      type: 'payment',
      description: 'پرداخت ۱,۵۰۰,۰۰۰ تومان از فاطمه کریمی',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      actionRequired: false
    },
    {
      id: 3,
      type: 'review',
      description: 'نظر جدید ۵ ستاره از محمد رضایی',
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
      actionRequired: false
    },
    {
      id: 4,
      type: 'profile',
      description: 'پروفایل شما بروزرسانی شد',
      timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000),
      actionRequired: false
    }
  ];

  // Mock data for appointments
  const mockAppointments = [
    {
      id: 1,
      clientName: 'سارا احمدی',
      type: 'مشاوره آنلاین',
      dateTime: new Date(Date.now() + 25 * 60 * 1000),
      duration: 60
    },
    {
      id: 2,
      clientName: 'حسن کریمی',
      type: 'جلسه حضوری',
      dateTime: new Date(Date.now() + 4 * 60 * 60 * 1000),
      duration: 90
    },
    {
      id: 3,
      clientName: 'مریم رضایی',
      type: 'مشاوره تلفنی',
      dateTime: new Date(Date.now() + 24 * 60 * 60 * 1000),
      duration: 45
    }
  ];

  // Filter consultations based on search and filters
  useEffect(() => {
    let filtered = mockConsultations;

    // Apply search filter
    if (searchQuery) {
      filtered = filtered?.filter(consultation =>
        consultation?.clientName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        consultation?.question?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        consultation?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Apply status filter
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(consultation => consultation?.status === filters?.status);
    }

    // Apply urgency filter
    if (filters?.urgency !== 'all') {
      filtered = filtered?.filter(consultation => consultation?.urgency === filters?.urgency);
    }

    // Apply category filter
    if (filters?.category !== 'all') {
      filtered = filtered?.filter(consultation => consultation?.category === filters?.category);
    }

    setFilteredConsultations(filtered);
  }, [searchQuery, filters]);

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleConsultationAccept = (consultationId) => {
    console.log('Accept consultation:', consultationId);
    // Navigate to consultation chat or response page
  };

  const handleConsultationViewDetails = (consultationId) => {
    console.log('View consultation details:', consultationId);
    // Navigate to detailed consultation view
  };

  const handleJoinMeeting = (appointmentId) => {
    console.log('Join meeting:', appointmentId);
    // Open video call interface
  };

  const handleRescheduleAppointment = (appointmentId) => {
    console.log('Reschedule appointment:', appointmentId);
    // Open reschedule modal
  };

  const handleNewConsultation = () => {
    console.log('Start new consultation');
    // Navigate to new consultation interface
  };

  const handleUpdateProfile = () => {
    navigate('/lawyer-profile-setup');
  };

  const handleViewReports = () => {
    console.log('View reports');
    // Navigate to reports page
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar Navigation */}
      <LawyerSidebarNavigation
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={handleSidebarToggle}
      />
      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'mr-16' : 'mr-64'} lg:mr-64 lg:${sidebarCollapsed ? 'mr-16' : 'mr-64'}`}>
        <div className="p-6 pb-20 lg:pb-6">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  داشبورد وکیل
                </h1>
                <p className="text-muted-foreground">
                  مدیریت مشاوره‌ها و فعالیت‌های حرفه‌ای
                </p>
              </div>
              
              <div className="flex items-center space-x-reverse space-x-4">
                <div className="flex items-center space-x-reverse space-x-2 text-sm text-muted-foreground">
                  <Icon name="Clock" size={16} />
                  <span>آخرین بروزرسانی: همین الان</span>
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  iconName="RefreshCw"
                  iconPosition="right"
                  onClick={() => window.location?.reload()}
                >
                  بروزرسانی
                </Button>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {mockStats?.map((stat, index) => (
              <StatsWidget
                key={index}
                title={stat?.title}
                value={stat?.value}
                change={stat?.change}
                changeType={stat?.changeType}
                icon={stat?.icon}
                color={stat?.color}
              />
            ))}
          </div>

          {/* Main Dashboard Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Column - Consultations */}
            <div className="lg:col-span-8">
              {/* Consultation Filters */}
              <ConsultationFilters
                onFilterChange={handleFilterChange}
                onSearch={handleSearch}
                totalCount={filteredConsultations?.length}
              />

              {/* Consultations List */}
              <div className="space-y-6">
                {filteredConsultations?.length === 0 ? (
                  <div className="bg-card border border-border rounded-lg p-12 text-center">
                    <Icon name="MessageSquare" size={64} className="text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-card-foreground mb-2">
                      مشاوره‌ای یافت نشد
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      {searchQuery || filters?.status !== 'all' || filters?.urgency !== 'all' || filters?.category !== 'all' ?'با فیلترهای انتخابی مشاوره‌ای یافت نشد' :'هنوز مشاوره‌ای دریافت نکرده‌اید'
                      }
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSearchQuery('');
                        setFilters({ status: 'all', urgency: 'all', category: 'all' });
                      }}
                    >
                      پاک کردن فیلترها
                    </Button>
                  </div>
                ) : (
                  filteredConsultations?.map((consultation) => (
                    <ConsultationCard
                      key={consultation?.id}
                      consultation={consultation}
                      onAccept={handleConsultationAccept}
                      onViewDetails={handleConsultationViewDetails}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Right Column - Sidebar Widgets */}
            <div className="lg:col-span-4 space-y-6">
              {/* Quick Actions */}
              <QuickActions
                onNewConsultation={handleNewConsultation}
                onUpdateProfile={handleUpdateProfile}
                onViewReports={handleViewReports}
              />

              {/* Upcoming Appointments */}
              <UpcomingAppointments
                appointments={mockAppointments}
                onJoinMeeting={handleJoinMeeting}
                onReschedule={handleRescheduleAppointment}
              />

              {/* Activity Feed */}
              <ActivityFeed activities={mockActivities} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LawyerDashboard;